import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useUndoDelete } from "@/lib/useUndoDelete";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { ADMIN } from "@/constants/testIds";
import { Trash2, GripVertical, Plus, X, Eye, EyeOff, LayoutTemplate, Upload, Download, History } from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import PageSectionsEditor from "@/components/PageSectionsEditor";
import { defaultPathwayIntroSections } from "@/components/sectionTypes";
import ListFilter from "@/components/admin/ListFilter";

export default function PathwaysPanel({ blogs, pathways, refresh }) {
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [selected, setSelected] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [localOrder, setLocalOrder] = useState(pathways);
  const [pathwayFilter, setPathwayFilter] = useState("");
  const [importing, setImporting] = useState(false);
  const [importReport, setImportReport] = useState(null);
  const fileInputRef = useRef(null);
  // Case-insensitive title / pathway_id filter. Reorder is disabled while
  // filtering so drag indices don't get out of sync with the underlying array.
  const filteredPathways = useMemo(() => {
    const q = pathwayFilter.trim().toLowerCase();
    if (!q) return localOrder;
    return localOrder.filter(p =>
      (p.title || "").toLowerCase().includes(q) ||
      (p.pathway_id || "").toLowerCase().includes(q)
    );
  }, [localOrder, pathwayFilter]);
  // Per-pathway intro editor state: which pathway's intro is open, plus its initial sections.
  const [introEditor, setIntroEditor] = useState({ open: false, pathway: null, sections: [] });
  useEffect(() => { setLocalOrder(pathways); }, [pathways]);

  const reset = () => { setTitle(""); setDesc(""); setSelected([]); setEditingId(null); };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    if (editingId) {
      await api.put(`/pathways/${editingId}`, { title, description: desc, blog_ids: selected });
      toast.success("Pathway updated");
    } else {
      await api.post("/pathways", { title, description: desc, blog_ids: selected });
      toast.success("Pathway created");
    }
    reset();
    refresh();
  };
  const edit = (p) => { setEditingId(p.pathway_id); setTitle(p.title); setDesc(p.description || ""); setSelected(p.blog_ids || []); window.scrollTo({top:0,behavior:"smooth"}); };
  const undoDelete = useUndoDelete();
  const del = (p) => undoDelete.run({
    label: `Deleted pathway "${p.title}"`,
    deletePath: `/pathways/${p.pathway_id}`,
    restorePath: `/pathways/${p.pathway_id}/restore`,
    onDone: refresh,
  });

  const openIntroEditor = async (p) => {
    // Fetch the full pathway so we have the current intro_sections array.
    try {
      const r = await api.get(`/pathways/${p.slug}`);
      setIntroEditor({ open: true, pathway: p, sections: r.data.intro_sections || [] });
    } catch {
      setIntroEditor({ open: true, pathway: p, sections: [] });
    }
  };
  const saveIntro = async (next) => {
    if (!introEditor.pathway) return;
    await api.put(`/pathways/${introEditor.pathway.pathway_id}`, { intro_sections: next });
    refresh();
  };

  const toggleVisibility = async (p, next) => {
    // Optimistic UI so the switch flips instantly.
    setLocalOrder(cur => cur.map(x => x.pathway_id === p.pathway_id ? { ...x, visible: next } : x));
    try {
      await api.put(`/pathways/${p.pathway_id}/visibility`, { visible: next });
      toast.success(next ? "Pathway visible to readers" : "Pathway hidden from readers");
      refresh();
    } catch (e) {
      setLocalOrder(cur => cur.map(x => x.pathway_id === p.pathway_id ? { ...x, visible: !next } : x));
      toast.error(e.response?.data?.detail || "Could not update visibility");
    }
  };

  const add = (id) => { if (!selected.includes(id)) setSelected([...selected, id]); };
  const remove = (id) => setSelected(selected.filter(x => x !== id));
  const onBlogsDragEnd = (result) => {
    if (!result.destination) return;
    const arr = [...selected];
    const [m] = arr.splice(result.source.index, 1);
    arr.splice(result.destination.index, 0, m);
    setSelected(arr);
  };
  const onDragEnd = async (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    const arr = [...localOrder];
    const [moved] = arr.splice(result.source.index, 1);
    arr.splice(result.destination.index, 0, moved);
    setLocalOrder(arr);
    try {
      await api.post("/pathways/reorder", { ids: arr.map(p => p.pathway_id) });
      toast.success("Order updated");
      refresh();
    } catch { toast.error("Reorder failed"); setLocalOrder(pathways); }
  };
  const available = blogs.filter(b => !selected.includes(b.blog_id));

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportReport(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/pathways/import-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setImportReport(data);
      const parts = [];
      if (data.created) parts.push(`${data.created} added`);
      if (data.updated) parts.push(`${data.updated} updated`);
      if (data.skipped_rows) parts.push(`${data.skipped_rows} rows skipped`);
      toast.success(`CSV imported — ${parts.join(", ") || "no rows"}`, { duration: 6000 });
      if (data.unknown_blog_count > 0) {
        toast.warning(`${data.unknown_blog_count} blog ID${data.unknown_blog_count === 1 ? "" : "s"} not in library yet — see report below`);
      }
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "CSV import failed");
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleExport = async () => {
    try {
      const res = await api.get("/pathways/export-csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers?.["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      a.download = match ? match[1] : "hospice-pathways.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Export failed");
    }
  };

  return (
    <Card className="border-slate-200 mt-6">
      <CardHeader>
        <CardTitle className="font-heading">
          {editingId ? "Edit pathway" : "Create pathway"}
          {editingId && <button onClick={reset} className="ml-3 text-xs uppercase tracking-widest text-slate-500 hover:text-slate-900">Cancel</button>}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form data-testid={ADMIN.createPathwayForm} onSubmit={submit} className="space-y-3">
          <div className="grid md:grid-cols-2 gap-4">
            <div><label className="text-xs uppercase tracking-widest text-slate-500">Title</label>
              <Input data-testid={ADMIN.pathwayTitle} value={title} onChange={e=>setTitle(e.target.value)} placeholder="e.g. Dealing with Death"/></div>
            <div><label className="text-xs uppercase tracking-widest text-slate-500">Description</label>
              <Input data-testid={ADMIN.pathwayDescription} value={desc} onChange={e=>setDesc(e.target.value)}/></div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Available blogs</div>
              <div className="border border-slate-200 rounded-md p-2 h-72 overflow-auto">
                {available.map(b => (
                  <button key={b.blog_id} type="button" data-testid={ADMIN.pathwayBlogToggle(b.blog_id)}
                    onClick={() => add(b.blog_id)}
                    className="w-full text-left px-2 py-1.5 rounded text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                    <Plus className="w-3.5 h-3.5 text-slate-400"/>{b.title}
                  </button>
                ))}
                {available.length === 0 && <div className="text-xs text-slate-400 p-2">No blogs available.</div>}
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Reading order (drag to reorder)</div>
              <DragDropContext onDragEnd={onBlogsDragEnd}>
                <Droppable droppableId="pw">
                  {(provided) => (
                    <ol ref={provided.innerRef} {...provided.droppableProps}
                        className="border border-slate-200 rounded-md p-2 h-72 overflow-auto space-y-1">
                      {selected.map((id, i) => {
                        const b = blogs.find(x => x.blog_id === id);
                        if (!b) return null;
                        return (
                          <Draggable key={id} draggableId={id} index={i}>
                            {(prov, snap) => (
                              <li ref={prov.innerRef} {...prov.draggableProps}
                                className={`flex items-center gap-2 text-sm px-2 py-1.5 rounded border ${snap.isDragging ? "bg-sky-50 border-sky-300" : "bg-white border-slate-200"}`}>
                                <span {...prov.dragHandleProps} className="text-slate-400 cursor-grab"><GripVertical className="w-4 h-4"/></span>
                                <span className="text-slate-400 w-6 text-right">{i+1}.</span>
                                <span className="flex-1 truncate">{b.title}</span>
                                <button type="button" onClick={() => remove(id)} className="text-slate-400 hover:text-red-600"><X className="w-4 h-4"/></button>
                              </li>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                      {selected.length === 0 && <div className="text-xs text-slate-400 p-2">Add blogs from the left.</div>}
                    </ol>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          </div>
          <Button data-testid={ADMIN.pathwaySubmit} type="submit" className="bg-sky-700 hover:bg-sky-600">
            {editingId ? "Save changes" : "Create pathway"}
          </Button>
        </form>

        {/* Bulk CSV controls */}
        <div className="mt-8 border-t border-slate-200 pt-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="font-heading font-semibold text-slate-900">Bulk import / export</div>
              <p className="text-xs text-slate-500 mt-1 max-w-2xl">
                Import a flat spreadsheet of <span className="font-medium">pathway → blog</span> rows in one go. Pathways are matched by <code className="text-[10px]">pathway_id</code> (e.g. HS-PATHWAY-001) — existing pathways update in place, new ones are added. Missing blog IDs are reported so you can upload the matching docx afterwards.
              </p>
            </div>
            <div className="flex gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={handleImport}
                className="hidden"
                data-testid="admin-pathways-csv-input"
              />
              <Button
                data-testid="admin-pathways-import-csv"
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
                variant="outline"
                className="gap-2"
                type="button"
              >
                <Upload className="w-4 h-4"/>
                {importing ? "Importing…" : "Import CSV"}
              </Button>
              <Button
                data-testid="admin-pathways-export-csv"
                onClick={handleExport}
                variant="outline"
                className="gap-2"
                type="button"
              >
                <Download className="w-4 h-4"/>
                Export CSV
              </Button>
            </div>
          </div>
          {importReport && (
            <div
              data-testid="admin-pathways-import-report"
              className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-4 text-sm"
            >
              <div className="flex flex-wrap gap-4">
                <span><strong className="text-emerald-700">{importReport.created}</strong> added</span>
                <span><strong className="text-sky-700">{importReport.updated}</strong> updated</span>
                {importReport.skipped_rows > 0 && (
                  <span><strong className="text-slate-500">{importReport.skipped_rows}</strong> rows skipped</span>
                )}
                {importReport.unknown_blog_count > 0 && (
                  <span><strong className="text-amber-700">{importReport.unknown_blog_count}</strong> unknown blog IDs</span>
                )}
              </div>
              {importReport.unknown_blog_ids?.length > 0 && (
                <div className="mt-2 text-xs text-amber-800">
                  <div className="font-medium mb-1">Blog IDs not in library yet (pathway rows still saved — upload the docx to link):</div>
                  <div className="font-mono text-[11px] leading-relaxed break-all">
                    {importReport.unknown_blog_ids.join(", ")}
                    {importReport.unknown_blog_count > importReport.unknown_blog_ids.length && (
                      <span> …and {importReport.unknown_blog_count - importReport.unknown_blog_ids.length} more.</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-8">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">
            Existing pathways ({filteredPathways.length}{pathwayFilter && filteredPathways.length !== localOrder.length ? ` of ${localOrder.length}` : ""})
            {!pathwayFilter && <span className="text-slate-400 normal-case"> — drag <GripVertical className="inline w-3 h-3 -mt-0.5"/> to reorder</span>}
            {pathwayFilter && <span className="text-slate-400 normal-case"> — clear filter to reorder</span>}
          </div>
          {localOrder.length > 0 && (
            <ListFilter
              value={pathwayFilter}
              onChange={setPathwayFilter}
              placeholder="Filter pathways by title or ID…"
              testId="admin-pathways-filter"
              className="mb-2"
            />
          )}
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="pathways-list">
              {(provided) => (
                <ol data-testid="pathways-drag-list" ref={provided.innerRef} {...provided.droppableProps}
                    className="divide-y divide-slate-200 border-y border-slate-200 text-sm">
                  {filteredPathways.map((p, displayIdx) => {
                    // Use the underlying-array index for draggableId indexing so drag
                    // reordering (only allowed when unfiltered) touches the right slot.
                    const i = localOrder.findIndex(x => x.pathway_id === p.pathway_id);
                    return (
                    <Draggable key={p.pathway_id} draggableId={p.pathway_id} index={i}
                               isDragDisabled={!!pathwayFilter}>
                      {(prov, snap) => (
                        <li ref={prov.innerRef} {...prov.draggableProps} data-testid={`pathway-row-${p.pathway_id}`}
                            className={`py-3 flex items-center justify-between gap-2 ${snap.isDragging ? "bg-sky-50 shadow-md rounded" : ""}`}>
                          <span {...prov.dragHandleProps} data-testid={`pathway-drag-${p.pathway_id}`}
                                className={`text-slate-400 pl-1 ${pathwayFilter ? "opacity-30 cursor-not-allowed" : "cursor-grab active:cursor-grabbing"}`}
                                title={pathwayFilter ? "Clear filter to reorder" : "Drag to reorder"}>
                            <GripVertical className="w-4 h-4"/>
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center">
                              <span className="text-slate-400 mr-3 font-mono text-xs">{String(i+1).padStart(2,"0")}</span>
                              <span className={`truncate ${p.visible === false ? "text-slate-500 italic" : ""}`}>{p.title}</span>
                              <span className="text-slate-400 ml-2 text-xs">· {p.resource_count ?? p.blog_ids?.length ?? 0} resource{(p.resource_count ?? p.blog_ids?.length ?? 0) === 1 ? "" : "s"}</span>
                              {p.visible === false && (
                                <span data-testid={`pathway-hidden-badge-${p.pathway_id}`}
                                      className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-slate-200 text-slate-700 border border-slate-300 rounded px-1.5 py-0.5">
                                  <EyeOff className="w-3 h-3"/>Hidden
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            <div className="flex items-center gap-1.5" title={p.visible === false ? "Hidden from readers" : "Visible to readers"}>
                              {p.visible === false ? <EyeOff className="w-3.5 h-3.5 text-slate-400"/> : <Eye className="w-3.5 h-3.5 text-emerald-600"/>}
                              <Switch data-testid={`pathway-visibility-${p.pathway_id}`}
                                      checked={p.visible !== false}
                                      onCheckedChange={(v) => toggleVisibility(p, v)}
                                      aria-label="Toggle visibility"/>
                            </div>
                            <button onClick={()=>openIntroEditor(p)}
                                    data-testid={`pathway-intro-${p.pathway_id}`}
                                    className="text-slate-500 hover:text-sky-700 text-xs uppercase tracking-widest inline-flex items-center gap-1"
                                    title="Edit pathway intro sections (Visual Editor)">
                              <LayoutTemplate className="w-3.5 h-3.5"/>Intro
                            </button>
                            <Popover>
                              <PopoverTrigger asChild>
                                <button
                                  data-testid={`pathway-history-${p.pathway_id}`}
                                  className="text-slate-500 hover:text-sky-700 text-xs uppercase tracking-widest inline-flex items-center gap-1"
                                  title="Change log">
                                  <History className="w-3.5 h-3.5"/>History
                                </button>
                              </PopoverTrigger>
                              <PopoverContent
                                data-testid={`pathway-history-popover-${p.pathway_id}`}
                                align="end" className="w-80 p-3 text-sm">
                                <div className="font-heading font-semibold text-slate-900 dark:text-slate-100 mb-2">
                                  Change log
                                </div>
                                <PathwayChangeLog log={p.change_log} />
                              </PopoverContent>
                            </Popover>
                            <button onClick={()=>edit(p)} className="text-sky-700 hover:text-sky-900 text-xs uppercase tracking-widest">Edit</button>
                            <button data-testid={`admin-pathway-delete-${p.pathway_id}`} onClick={()=>del(p)} title="Delete (with Undo)" className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4"/></button>
                          </div>
                        </li>
                      )}
                    </Draggable>
                    );
                  })}
                  {provided.placeholder}
                  {filteredPathways.length === 0 && (
                    <li className="py-6 text-slate-400 text-center">
                      {localOrder.length === 0 ? "No pathways yet." : `No pathways match "${pathwayFilter}".`}
                    </li>
                  )}
                </ol>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      </CardContent>

      <PageSectionsEditor
        open={introEditor.open}
        onOpenChange={(v) => setIntroEditor(prev => ({ ...prev, open: v }))}
        title={introEditor.pathway ? `Edit intro — ${introEditor.pathway.title}` : "Edit pathway intro"}
        resourceKey={introEditor.pathway ? `pathway_intro:${introEditor.pathway.pathway_id}` : null}
        initialSections={introEditor.sections.length ? introEditor.sections : defaultPathwayIntroSections(introEditor.pathway)}
        onSave={saveIntro}
      />
    </Card>
  );
}

// ---- Change-log sub-component -----------------------------------------------
const EVENT_LABEL = {
  auto_hidden: "Auto-hidden",
  auto_restored: "Auto-restored",
  admin_hidden: "Manually hidden",
  admin_shown: "Manually shown",
};
const EVENT_COLOR = {
  auto_hidden: "bg-slate-100 text-slate-700 border-slate-300",
  auto_restored: "bg-emerald-50 text-emerald-800 border-emerald-200",
  admin_hidden: "bg-amber-50 text-amber-800 border-amber-200",
  admin_shown: "bg-sky-50 text-sky-800 border-sky-200",
};

function formatWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}

function PathwayChangeLog({ log }) {
  const entries = Array.isArray(log) ? [...log].reverse() : [];
  if (entries.length === 0) {
    return (
      <div data-testid="pathway-history-empty" className="text-slate-500 text-xs italic">
        No visibility changes yet.
      </div>
    );
  }
  return (
    <ol data-testid="pathway-history-list" className="space-y-2 max-h-64 overflow-y-auto">
      {entries.map((e, i) => (
        <li key={i} className="flex items-start gap-2 text-xs">
          <span className={`inline-flex shrink-0 items-center px-1.5 py-0.5 rounded border uppercase tracking-widest text-[10px] font-medium ${EVENT_COLOR[e.event] || "bg-slate-100 text-slate-700 border-slate-300"}`}>
            {EVENT_LABEL[e.event] || e.event}
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-slate-700 dark:text-slate-300 truncate">{e.reason || "—"}</div>
            <div className="text-slate-400">{formatWhen(e.at)}</div>
          </div>
        </li>
      ))}
    </ol>
  );
}
